// useJourneyMaterials.ts
// ======================
// Custom hook for managing journey materials (uploaded files) with Firebase CRUD
// Feature #78 - React hook for material upload/delete/fetch

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  uploadJourneyMaterial,
  getJourneyMaterials,
  deleteJourneyMaterial,
  updateMaterialExtractedText,
} from '../services/firebase';
import i18n from '../i18n';
import type { JourneyMaterial } from '../types';

// Helper to convert Firebase Timestamp to Date/string
function normalizeMaterialDates(material: Record<string, unknown>): JourneyMaterial {
  const normalized = { ...material } as unknown as JourneyMaterial;

  // Handle createdAt conversion
  if (material.createdAt) {
    if (typeof material.createdAt === 'object' && 'toDate' in (material.createdAt as object)) {
      // Firebase Timestamp
      normalized.createdAt = (material.createdAt as { toDate: () => Date }).toDate().toISOString();
    } else if (material.createdAt instanceof Date) {
      normalized.createdAt = material.createdAt.toISOString();
    }
  }

  // Handle updatedAt conversion
  if (material.updatedAt) {
    if (typeof material.updatedAt === 'object' && 'toDate' in (material.updatedAt as object)) {
      normalized.updatedAt = (material.updatedAt as { toDate: () => Date }).toDate().toISOString();
    } else if (material.updatedAt instanceof Date) {
      normalized.updatedAt = material.updatedAt.toISOString();
    }
  }

  return normalized;
}

export interface UseJourneyMaterialsResult {
  // Data
  materials: JourneyMaterial[];
  loading: boolean;
  uploading: boolean;
  error: string | null;

  // Actions
  fetchMaterials: (journeyId: string) => Promise<void>;
  uploadMaterial: (journeyId: string, file: File) => Promise<{ success: boolean; material?: JourneyMaterial; error?: string }>;
  deleteMaterial: (materialId: string) => Promise<{ success: boolean; error?: string }>;
  updateExtractedText: (materialId: string, text: string) => Promise<{ success: boolean; error?: string }>;
  clearMaterials: () => void;
}

export function useJourneyMaterials(): UseJourneyMaterialsResult {
  const { user, isMockAuth } = useAuth();
  const [materials, setMaterials] = useState<JourneyMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentJourneyId, setCurrentJourneyId] = useState<string | null>(null);

  // LocalStorage key for mock auth fallback
  const getLocalStorageKey = (journeyId: string) =>
    user?.uid ? `lyra_materials_${user.uid}_${journeyId}` : `lyra_materials_${journeyId}`;

  // Fetch all materials for a journey
  const fetchMaterials = useCallback(async (journeyId: string) => {
    if (!user?.uid) {
      setMaterials([]);
      setLoading(false);
      return;
    }

    setCurrentJourneyId(journeyId);
    setLoading(true);
    setError(null);

    try {
      if (isMockAuth) {
        // Use localStorage for mock auth (development)
        const localKey = getLocalStorageKey(journeyId);
        const saved = localStorage.getItem(localKey);
        const parsed = saved ? JSON.parse(saved) : [];
        setMaterials(parsed);
      } else {
        // Use Firebase for real auth
        const result = await getJourneyMaterials(user.uid, journeyId);
        if (result.success && result.materials) {
          // Normalize dates from Firebase Timestamps
          const normalized = result.materials.map((m) =>
            normalizeMaterialDates(m as unknown as Record<string, unknown>)
          );
          setMaterials(normalized);
        } else {
          setError(result.error || 'Kunne ikke hente materialer');
          setMaterials([]);
        }
      }
    } catch (err) {
      console.error('[useJourneyMaterials] Error fetching materials:', err);
      setError(i18n.t('errors:fetch.materials'));
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, isMockAuth]);

  // Upload a new material
  const uploadMaterial = useCallback(async (
    journeyId: string,
    file: File
  ): Promise<{ success: boolean; material?: JourneyMaterial; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    setUploading(true);
    setError(null);

    try {
      if (isMockAuth) {
        // Mock auth: simulate upload with localStorage
        const extension = file.name.split('.').pop()?.toLowerCase();
        const validTypes = ['pdf', 'docx', 'txt'];

        if (!extension || !validTypes.includes(extension)) {
          setUploading(false);
          return {
            success: false,
            error: `Ugyldigt filformat. Understøttede formater: ${validTypes.join(', ')}`,
          };
        }

        // Create mock material
        const newMaterial: JourneyMaterial = {
          id: `material_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          userId: user.uid,
          journeyId,
          fileName: file.name,
          fileType: extension as JourneyMaterial['fileType'],
          fileSize: file.size,
          storagePath: `mock/path/${file.name}`,
          downloadUrl: URL.createObjectURL(file), // Create blob URL for mock
          createdAt: new Date().toISOString(),
        };

        // Save to localStorage
        const localKey = getLocalStorageKey(journeyId);
        const existing = JSON.parse(localStorage.getItem(localKey) || '[]');
        existing.push(newMaterial);
        localStorage.setItem(localKey, JSON.stringify(existing));

        // Update state if we're viewing this journey
        if (currentJourneyId === journeyId) {
          setMaterials(existing);
        }

        setUploading(false);
        return { success: true, material: newMaterial };
      } else {
        // Firebase: use upload function
        const result = await uploadJourneyMaterial(user.uid, journeyId, file);

        if (result.success && result.material) {
          // Refresh materials list if we're viewing this journey
          if (currentJourneyId === journeyId) {
            await fetchMaterials(journeyId);
          }

          setUploading(false);
          return { success: true, material: result.material };
        } else {
          setUploading(false);
          return { success: false, error: result.error || 'Kunne ikke uploade fil' };
        }
      }
    } catch (err) {
      console.error('[useJourneyMaterials] Error uploading material:', err);
      setUploading(false);
      return { success: false, error: 'Der opstod en fejl ved upload af fil' };
    }
  }, [user?.uid, isMockAuth, currentJourneyId, fetchMaterials]);

  // Delete a material
  const deleteMaterial = useCallback(async (
    materialId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: remove from localStorage
        if (currentJourneyId) {
          const localKey = getLocalStorageKey(currentJourneyId);
          const existing = JSON.parse(localStorage.getItem(localKey) || '[]');
          const filtered = existing.filter((m: JourneyMaterial) => m.id !== materialId);
          localStorage.setItem(localKey, JSON.stringify(filtered));
          setMaterials(filtered);
        }
        return { success: true };
      } else {
        // Firebase: use delete function
        const result = await deleteJourneyMaterial(user.uid, materialId);

        if (result.success) {
          // Update local state
          setMaterials(prev => prev.filter(m => m.id !== materialId));
          return { success: true };
        } else {
          return { success: false, error: result.error || 'Kunne ikke slette materiale' };
        }
      }
    } catch (err) {
      console.error('[useJourneyMaterials] Error deleting material:', err);
      return { success: false, error: 'Der opstod en fejl ved sletning af materiale' };
    }
  }, [user?.uid, isMockAuth, currentJourneyId]);

  // Update extracted text for a material
  const updateExtractedText = useCallback(async (
    materialId: string,
    text: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: update in localStorage
        if (currentJourneyId) {
          const localKey = getLocalStorageKey(currentJourneyId);
          const existing = JSON.parse(localStorage.getItem(localKey) || '[]');
          const index = existing.findIndex((m: JourneyMaterial) => m.id === materialId);
          if (index !== -1) {
            existing[index].extractedText = text;
            existing[index].updatedAt = new Date().toISOString();
            localStorage.setItem(localKey, JSON.stringify(existing));
            setMaterials(existing);
          }
        }
        return { success: true };
      } else {
        // Firebase: use update function
        const result = await updateMaterialExtractedText(materialId, text);

        if (result.success) {
          // Update local state
          setMaterials(prev =>
            prev.map(m =>
              m.id === materialId
                ? { ...m, extractedText: text, updatedAt: new Date().toISOString() }
                : m
            )
          );
          return { success: true };
        } else {
          return { success: false, error: result.error || 'Kunne ikke opdatere tekst' };
        }
      }
    } catch (err) {
      console.error('[useJourneyMaterials] Error updating extracted text:', err);
      return { success: false, error: 'Der opstod en fejl ved opdatering af tekst' };
    }
  }, [user?.uid, isMockAuth, currentJourneyId]);

  // Clear materials (when switching journeys)
  const clearMaterials = useCallback(() => {
    setMaterials([]);
    setCurrentJourneyId(null);
    setError(null);
  }, []);

  return {
    materials,
    loading,
    uploading,
    error,
    fetchMaterials,
    uploadMaterial,
    deleteMaterial,
    updateExtractedText,
    clearMaterials,
  };
}

export default useJourneyMaterials;
